import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Document,
  Folder,
  GetFoldersWithDocumentCountQuery,
  GetFoldersWithDocumentCountDocument,
  SoftDeleteDocumentDocument,
  UpdateDocumentDocument
} from '../../../../gql/graphql';
import { useNotificationService } from '../../../Notification';
import { useKeycloakAuth } from '../../../../contexts/KeycloakAuthProvider';
import { useAgentApi } from '../../../../contexts/ApiContext';
import { useFileUpload } from './useFileUpload';
import { useLazyFolderData } from './useLazyFolderData';
import { useAdminTranslation, useCommonTranslation } from '../../../../modules/i18n';

// Type for folders returned from GetFoldersWithDocumentCount query
type FolderWithCount = GetFoldersWithDocumentCountQuery['findManyFolders'][number];

interface UseDocumentsReturn {
  // State
  documents: Document[];
  folders: FolderWithCount[];
  loading: boolean;
  error: string | null;
  showDocumentModal: boolean;
  editingDocument: Document | null;
  showImportModal: boolean;
  importFolderId: string;
  deletingDocumentIds: string[];
  deletingFolderIds: string[];

  // Actions
  handleCreateDocument: () => void;
  handleEditDocument: (document: Document) => void;
  handleDeleteDocument: (documentId: string) => void;
  handleDeleteDocuments: (documentIds: string[]) => Promise<boolean>;
  handleDeleteSelection: (documentIds: string[], folderIds: string[]) => Promise<boolean>;
  handleSaveDocument: (documentData: Partial<Document>) => void;
  handleImportToFolder: (folderId: string) => void;
  handleImportFiles: (files: File[], folderId: string) => Promise<void>;
  handleUploadFolder: () => void;
  handleImportComplete: () => Promise<void>;
  handleDeleteFolder: (folderIds: string[]) => Promise<boolean>;
  refetchDocuments: () => void;
  refetchFolders: () => void;

  // Modal controls
  setShowDocumentModal: (show: boolean) => void;
  setShowImportModal: (show: boolean) => void;
  setImportFolderId: (id: string) => void;

  // Lazy loading controls
  loadFolderDocuments: (folderId: string) => Promise<void>;
  prefetchFolderDocuments: (folderId: string) => void;
  isFolderLoading: (folderId: string) => boolean;
  getFolderDocuments: (folderId: string) => Document[];
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const useDocuments = (confirmDialog: ConfirmDialogState, setConfirmDialog: (dialog: ConfirmDialogState | ((prev: ConfirmDialogState) => ConfirmDialogState)) => void): UseDocumentsReturn => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFolderId, setImportFolderId] = useState<string>('');
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<string[]>([]);
  const [deletingFolderIds, setDeletingFolderIds] = useState<string[]>([]);

  const { showSuccess, showError } = useNotificationService();
  const { user } = useKeycloakAuth();
  const agentApi = useAgentApi();
  const { t } = useAdminTranslation();
  const { t: tCommon } = useCommonTranslation();
  const userId = user?.id || '';

  // Initialize lazy loading hook for documents
  const {
    folderDataMap,
    loadFolderDocuments: loadFolderDocs,
    prefetchFolderDocuments: prefetchFolderDocs,
    isLoading: isFolderLoading,
    getDocuments: getFolderDocs,
    clearFolderData,
    removeDocument,
    removeDocuments
  } = useLazyFolderData();

  // Use the dedicated file upload hook
  const { isUploading, fileStatuses: _fileStatuses, handleFileUpload } = useFileUpload(userId, () => {
    // Ensure both documents and folders are refreshed after upload
    refetchFolders();
    // Clear lazy loaded data after upload
    // Note: We'll reload when folders are expanded again
  });

  // GraphQL queries - Use optimized folder query with counts only
  const {
    data: foldersData,
    loading: foldersLoading,
    error: foldersError,
    refetch: refetchFolders
  } = useQuery(GetFoldersWithDocumentCountDocument, {
    skip: !userId
  });

  // GraphQL mutations
  const [softDeleteDocumentMutation] = useMutation(SoftDeleteDocumentDocument);
  const [updateDocumentMutation] = useMutation(UpdateDocumentDocument);

  // Derived state
  const folders: FolderWithCount[] = foldersData?.findManyFolders || [];
  const loading = foldersLoading || isUploading;
  const error = foldersError?.message || null;

  // Aggregate all loaded documents from lazy loading for backward compatibility
  // This is mainly for search and delete operations
  const documents: Document[] = [];
  // Note: Individual folder documents are accessed via getFolderDocuments(folderId)

  // Wrapper functions for lazy loading
  const loadFolderDocuments = async (folderId: string) => {
    await loadFolderDocs(folderId);
  };

  const prefetchFolderDocuments = (folderId: string) => {
    prefetchFolderDocs(folderId);
  };

  const getFolderDocuments = (folderId: string) => {
    return getFolderDocs(folderId);
  };

  // Clear all loaded folder caches — used by upload hooks after ingestion
  const refetchDocuments = () => {
    for (const folderId of Array.from(folderDataMap.keys())) {
      clearFolderData(folderId);
    }
  };

  const handleCreateDocument = () => {
    setEditingDocument(null);
    setShowDocumentModal(true);
  };

  const handleEditDocument = (document: Document) => {
    setEditingDocument(document);
    setShowDocumentModal(true);
  };

  const handleDeleteDocument = async (documentId: string, documentTitle?: string) => {
    const document = documents.find((d) => d.id === documentId);
    const title = documentTitle ?? document?.title;

    setConfirmDialog({
      isOpen: true,
      title: t('content.documents.messages.deleteDocument.title'),
      message: t('content.documents.messages.deleteDocument.message', { title } as any),
      onConfirm: async () => {
        try {
          setDeletingDocumentIds([documentId]);

          await softDeleteDocumentMutation({
            variables: { id: documentId, userId }
          });

          removeDocument(documentId);
          showSuccess(tCommon('status.success'), t('content.documents.messages.deleteDocument.success'));
        } catch (error: any) {
          console.error('Error deleting document:', error);
          showError(tCommon('status.error'), t('content.documents.messages.deleteDocument.error'));
        } finally {
          setDeletingDocumentIds([]);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
        }
      }
    });
  };

  const handleDeleteDocuments = async (documentIds: string[]): Promise<boolean> => {
    if (!documentIds || documentIds.length === 0) return false;

    setConfirmDialog({
      isOpen: true,
      title: t('content.documents.messages.deleteDocuments.title', { count: documentIds.length } as any),
      message: t('content.documents.messages.deleteDocuments.message', { count: documentIds.length } as any),
      onConfirm: async () => {
        try {
          setDeletingDocumentIds(documentIds);
          for (const id of documentIds) {
            await softDeleteDocumentMutation({
              variables: { id, userId }
            });
          }

          removeDocuments(documentIds);
          showSuccess(tCommon('status.success'), t('content.documents.messages.deleteDocuments.success'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
          console.error('Error deleting documents:', error);
          showError(tCommon('status.error'), t('content.documents.messages.deleteDocuments.error'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
          return false;
        } finally {
          setDeletingDocumentIds([]);
        }
      }
    });

    return false;
  };

  const handleSaveDocument = async (documentData: Partial<Document>) => {
    try {
      if (editingDocument) {
        // Update existing document
        await updateDocumentMutation({
          variables: {
            id: editingDocument.id,
            userId,
            data: {
              title: documentData.title,
              ...(documentData.folderId && { folderId: documentData.folderId })
            }
          }
        });

        await refetchDocuments();
        showSuccess(tCommon('status.success'), t('content.documents.messages.saveDocument.updateSuccess'));
      } else {
        showError(tCommon('status.error'), t('content.documents.messages.saveDocument.createError'));
      }

      setShowDocumentModal(false);
    } catch (error: any) {
      console.error('Error saving document:', error);
      showError(tCommon('status.error'), t('content.documents.messages.saveDocument.saveError'));
    }
  };

  const handleDeleteFolder = async (folderIds: string[]): Promise<boolean> => {
    const folder = folders.find((f) => folderIds.includes(f.id));

    setConfirmDialog({
      isOpen: true,
      title: t('content.documents.messages.deleteFolder.title'),
      message: t('content.documents.messages.deleteFolder.message', { name: folder?.name } as any),
      isLoading: false,
      onConfirm: async () => {
        try {
          // Set loading state
          setConfirmDialog({
            isOpen: true,
            title: t('content.documents.messages.deleteFolder.title'),
            message: t('content.documents.messages.deleteFolder.message', { name: folder?.name } as any),
            isLoading: true,
            onConfirm: () => {}
          });
          setDeletingFolderIds(folderIds);

          // Use modern orchestration endpoint to delete folder
          for (const folderId of folderIds) {
            await agentApi.deleteFolder(folderId, userId);
            clearFolderData(folderId);
          }

          await refetchFolders();
          showSuccess(tCommon('status.success'), t('content.documents.messages.deleteFolder.success'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
          console.error('Error deleting folder:', error);
          const isLinkedToAgents = error?.message?.includes('linked to agents');
          const errorMessage = isLinkedToAgents
            ? t('content.documents.messages.deleteFolder.linkedToAgents')
            : t('content.documents.messages.deleteFolder.error');
          showError(tCommon('status.error'), errorMessage);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
          return false;
        } finally {
          setDeletingFolderIds([]);
        }
      }
    });

    return false;
  };

  // Delete a mixed selection with safeguards:
  // - If a folder is selected, do not send deletes for its descendants individually
  // - Delete folders after documents to avoid conflicts, but since folder API handles cascading, order is less critical
  const handleDeleteSelection = async (documentIds: string[], folderIds: string[]): Promise<boolean> => {
    setConfirmDialog({
      isOpen: true,
      title: t('content.documents.messages.deleteSelection.title'),
      message: t('content.documents.messages.deleteSelection.message'),
      isLoading: false,
      onConfirm: async () => {
        try {
          // Set loading state
          setConfirmDialog({
            isOpen: true,
            title: t('content.documents.messages.deleteSelection.title'),
            message: t('content.documents.messages.deleteSelection.message'),
            isLoading: true,
            onConfirm: () => {}
          });

          // Document deletions
          if (documentIds && documentIds.length > 0) {
            setDeletingDocumentIds(documentIds);
            for (const id of documentIds) {
              await softDeleteDocumentMutation({ variables: { id, userId } });
            }
            removeDocuments(documentIds);
          }

          // Folder deletions (each cascades on backend)
          if (folderIds && folderIds.length > 0) {
            setDeletingFolderIds(folderIds);
            // Sort folders top-down by depth to match expected behavior
            const parentOf: Record<string, string | undefined> = {};
            folders.forEach((f) => (parentOf[f.id] = f.parentId ?? undefined));
            const depthCache: Record<string, number> = {};
            const getDepth = (id: string): number => {
              if (depthCache[id] !== undefined) return depthCache[id];
              let depth = 0;
              let cur = parentOf[id];
              while (cur) {
                depth += 1;
                cur = parentOf[cur];
              }
              depthCache[id] = depth;
              return depth;
            };
            const sortedFolders = [...folderIds].sort((a, b) => getDepth(a) - getDepth(b));
            for (const folderId of sortedFolders) {
              await agentApi.deleteFolder(folderId, userId);
              clearFolderData(folderId);
            }
          }

          await refetchFolders();
          showSuccess(tCommon('status.success'), t('content.documents.messages.deleteSelection.success'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
          console.error('Error deleting selection:', error);
          showError(tCommon('status.error'), t('content.documents.messages.deleteSelection.error'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, isLoading: false }));
          return false;
        } finally {
          setDeletingDocumentIds([]);
          setDeletingFolderIds([]);
        }
      }
    });

    return false;
  };

  const handleImportToFolder = (folderId: string) => {
    setImportFolderId(folderId);
    setShowImportModal(true);
  };

  const handleUploadFolder = () => {
    setImportFolderId('');
    setShowImportModal(true);
  };

  const handleImportComplete = async (): Promise<void> => {
    await refetchFolders();
    setShowImportModal(false);
    showSuccess(tCommon('status.success'), t('content.documents.messages.importComplete'));
  };

  const handleImportFiles = async (files: File[], folderId: string): Promise<void> => {
    try {
      const folder = folders.find((f) => f.id === folderId);
      if (!folder) {
        showError(tCommon('status.error'), t('content.documents.import.errors.invalidFile'));
        return;
      }

      // Convert FolderWithCount to Folder type for upload
      const folderForUpload: Folder = {
        __typename: 'Folder',
        id: folder.id,
        name: folder.name,
        description: folder.description ?? null,
        parentId: folder.parentId ?? null,
        userId: folder.userId,
        createdAt: folder.createdAt,
        updatedAt: folder.updatedAt,
        // Default values for fields not in FolderWithCount
        agentFolders: [],
        subFolders: [],
        documents: [],
        _count: null
      };

      await handleFileUpload(files, folderId, folderForUpload);

      // Explicitly refetch to reflect new folders/documents immediately
      await refetchFolders();
      refetchDocuments();

      setShowImportModal(false);
      showSuccess(tCommon('status.success'), t('content.documents.import.buttons.uploading', { count: files.length }));
    } catch (error: any) {
      console.error('Error importing files:', error);
      showError(tCommon('status.error'), error.message || t('content.documents.import.errors.invalidFileType'));
    }
  };

  return {
    // State
    documents,
    folders,
    loading,
    error,
    showDocumentModal,
    editingDocument,
    showImportModal,
    importFolderId,
    deletingDocumentIds,
    deletingFolderIds,

    // Actions
    handleCreateDocument,
    handleEditDocument,
    handleDeleteDocument,
    handleDeleteDocuments,
    handleSaveDocument,
    handleImportToFolder,
    handleImportFiles,
    handleUploadFolder,
    handleImportComplete,
    handleDeleteFolder,
    handleDeleteSelection,
    refetchDocuments,
    refetchFolders,

    // Modal controls
    setShowDocumentModal,
    setShowImportModal,
    setImportFolderId,

    // Lazy loading controls
    loadFolderDocuments,
    prefetchFolderDocuments,
    isFolderLoading,
    getFolderDocuments
  };
};
